import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import { formatPercentage } from '../../../packages/utils/numberFormatters'
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { Stack } from '@mui/system'
import { RegisteredCheckBox } from '../../../components/fields/CheckBox'
import { feeLevelName } from '../../../components/entities/FeesInformation'
import { FeeDialog } from './AdminFeeDialog'
import { FeeFormWithId } from '../types/FeeForm'

interface FeeStructureFieldsProps {
    noGuarantorsChecked?: boolean
}

function FeeStructureFields({ noGuarantorsChecked }: FeeStructureFieldsProps) {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const formMethods = useFormContext<{ fees: FeeFormWithId[] }>()
    const formValues = formMethods.getValues()
    const [feeToEdit, setFeeToEdit] = useState<Partial<FeeFormWithId> | null>(null)
    const [feeFormOpen, setFeeFormOpen] = useState(false)
    const setFormFees = useCallback(
        (newFees: FeeFormWithId[] | null) => {
            if (!newFees) {
                // issue.fees = null
                if (formMethods.getValues().fees?.length !== 0) {
                    formMethods.setValue('fees', [])
                }
                return
            }

            formMethods.setValue('fees', newFees)
        },
        [formMethods],
    )

    const newFormOpen = () => {
        // find first available level (starting with 1)
        const level =
            (formValues.fees || []).reduce((acc, fee) => (+(fee.feeLevel ?? 0) > acc ? +(fee.feeLevel ?? 0) : acc), 0) +
            1
        setFeeToEdit({ feeLevel: level + '' })
        setFeeFormOpen(true)
    }
    const editFormOpen = (fee: FeeFormWithId) => {
        setFeeToEdit(fee)
        setFeeFormOpen(true)
    }
    const nullEmptyStrings = (obj: Record<string, unknown>) => {
        Object.keys(obj).forEach((key) => {
            if (obj[key] === '') obj[key] = null
        })
    }
    const handleDeleteFee = (fee: FeeFormWithId) => {
        const newFees = formValues.fees?.filter((f) => f !== fee)
        if (newFees) {
            setFormFees(newFees)
        }
    }
    const handleSubmitFee = (fee: FeeFormWithId) => {
        nullEmptyStrings(fee)

        if (fee.id) {
            setFormFees([...formValues.fees.filter((f) => f.id !== fee.id), fee])
            return
        }

        const newFees = [...(formValues.fees || []).filter((f) => f !== feeToEdit), fee]
        setFormFees(newFees)
    }
    return (
        <>
            {feeFormOpen && feeToEdit && (
                <FeeDialog
                    open={feeFormOpen}
                    setOpen={setFeeFormOpen}
                    fee={feeToEdit}
                    handleSubmitFee={handleSubmitFee}
                />
            )}
            <TableContainer>
                <Table size='small'>
                    <TableHead>
                        <TableRow>
                            <TableCell>Nivå</TableCell>
                            <TableCell>Namn</TableCell>
                            <TableCell>Arvode</TableCell>
                            <TableCell>Arvode units</TableCell>
                            <TableCell>Arvode båda</TableCell>
                            <TableCell>Top-down</TableCell>
                            <TableCell>Middle-down</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            <TableCell>0</TableCell>
                            <TableCell colSpan={7}>Åtagande</TableCell>
                        </TableRow>
                        {formValues.fees
                            ?.sort((a, b) => +(a.feeLevel ?? 0) - +(b.feeLevel ?? 0))
                            .map((fee) => (
                                <TableRow key={JSON.stringify(fee)}>
                                    <TableCell>{fee.feeLevel}</TableCell>
                                    <TableCell>{feeLevelName(formValues.fees, fee.feeLevel)}</TableCell>
                                    <TableCell>{formatPercentage(fee.feeCash, { locale, minDecimals: 0 })}</TableCell>
                                    <TableCell>{formatPercentage(fee.feeUnits, { locale, minDecimals: 0 })}</TableCell>
                                    <TableCell>{fee.feeBoth ? 'J' : 'N'}</TableCell>
                                    <TableCell>{fee.topDown ? 'J' : 'N'}</TableCell>
                                    <TableCell>
                                        {formatPercentage(fee.middleDown, { locale, minDecimals: 0 })}
                                    </TableCell>
                                    <TableCell>
                                        <Button onClick={() => editFormOpen(fee)}>Ändra</Button>
                                        <Button onClick={() => handleDeleteFee(fee)}>[Ta bort]</Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        <TableRow>
                            <TableCell colSpan={8}>
                                <Stack direction='row' spacing={2}>
                                    {!noGuarantorsChecked && (
                                        <Button onClick={newFormOpen} variant='outlined'>
                                            Ny nivå
                                        </Button>
                                    )}
                                    {(!formValues.fees || formValues.fees.length === 0) && (
                                        <RegisteredCheckBox name='noGuarantors' label='Inga garanter' />
                                    )}
                                </Stack>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}

export default FeeStructureFields
